import React, { useState, useEffect } from 'react';
import api from '../api';

const PERIOD_OPTIONS = [
  { label: '30 יום', days: 30 },
  { label: '90 יום', days: 90 },
  { label: '6 חודשים', days: 180 },
  { label: 'שנה', days: 365 },
];

export default function KeywordChart({ keywordId, keyword, onClose, additional }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    loadRankings(days);
  }, [keywordId, days]);

  async function loadRankings(d, isRetry) {
    setLoading(true);
    try {
      const result = additional
        ? await api.getAdditionalRankings(keywordId, d)
        : await api.getRankings(keywordId, d);
      // If no data in this range and we haven't tried max range yet, auto-expand
      if ((!result || !result.rankings || result.rankings.length === 0) && d < 365 && !isRetry) {
        return loadRankings(365, true);
      }
      setData(result);
      if (isRetry && result && result.rankings && result.rankings.length > 0) {
        setDays(365);
      }
    } catch (err) {
      console.error(err);
      setData(null);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', padding: '30px', color: '#8B99AE' }}>טוען היסטוריה...</div>
      </div>
    );
  }

  if (!data || !data.rankings || data.rankings.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <span style={{ fontWeight: 700 }}>{keyword}</span>
          <button onClick={onClose} style={closeBtnStyle}>&times;</button>
        </div>
        <div style={{ textAlign: 'center', padding: '30px', color: '#8B99AE' }}>אין נתוני היסטוריה</div>
      </div>
    );
  }

  // Filter out null positions for the chart, keep for display
  const rankings = data.rankings;
  const withPosition = rankings.filter(r => r.position_desktop !== null);

  if (withPosition.length === 0) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <span style={{ fontWeight: 700 }}>{keyword}</span>
          <button onClick={onClose} style={closeBtnStyle}>&times;</button>
        </div>
        <div style={{ textAlign: 'center', padding: '30px', color: '#8B99AE' }}>אין נתוני מיקום לתקופה זו</div>
      </div>
    );
  }

  // Chart calculations
  const positions = withPosition.map(r => r.position_desktop);
  const minPos = Math.min(...positions);
  const maxPos = Math.max(...positions);
  const bestPos = minPos;
  const worstPos = maxPos;
  const currentPos = positions[positions.length - 1];
  const firstPos = positions[0];
  const totalChange = firstPos - currentPos; // positive = improved

  // Chart dimensions
  const chartWidth = 700;
  const chartHeight = 200;
  const padLeft = 40;
  const padRight = 16;
  const padTop = 16;
  const padBottom = 24;
  const plotW = chartWidth - padLeft - padRight;
  const plotH = chartHeight - padTop - padBottom;

  // Y axis: position (inverted - 1 at top)
  const yMin = Math.max(1, minPos - 2);
  const yMax = maxPos + 2;
  const yRange = yMax - yMin || 1;

  function toX(i) {
    return padLeft + (i / (withPosition.length - 1 || 1)) * plotW;
  }
  function toY(pos) {
    return padTop + ((pos - yMin) / yRange) * plotH;
  }

  // Build SVG path
  const points = withPosition.map((r, i) => ({ x: toX(i), y: toY(r.position_desktop) }));
  const linePath = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');

  // Area fill path
  const areaPath = linePath + ` L ${points[points.length - 1].x} ${padTop + plotH} L ${points[0].x} ${padTop + plotH} Z`;

  // Y axis labels
  const yLabels = [];
  const step = yRange <= 10 ? 1 : yRange <= 30 ? 5 : 10;
  for (let p = Math.ceil(yMin / step) * step; p <= yMax; p += step) {
    yLabels.push(p);
  }

  // X axis labels (show ~6 dates)
  const xLabelCount = Math.min(6, withPosition.length);
  const xLabels = [];
  for (let i = 0; i < xLabelCount; i++) {
    const idx = Math.round(i * (withPosition.length - 1) / (xLabelCount - 1 || 1));
    const d = withPosition[idx].date;
    xLabels.push({ x: toX(idx), label: d.slice(5) }); // MM-DD
  }

  // Color based on current position
  const lineColor = currentPos <= 3 ? '#22C55E' : currentPos <= 10 ? '#22D3EE' : currentPos <= 20 ? '#F59E0B' : '#EF4444';

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontWeight: 700, fontSize: '15px' }}>{keyword}</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.days}
                onClick={() => setDays(opt.days)}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: '1px solid ' + (days === opt.days ? '#3B82F6' : '#1E293B'),
                  background: days === opt.days ? 'rgba(59,130,246,0.15)' : 'transparent',
                  color: days === opt.days ? '#3B82F6' : '#8B99AE',
                  fontSize: '11px',
                  fontFamily: "'Heebo', sans-serif",
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <button onClick={onClose} style={closeBtnStyle}>&times;</button>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '0', marginBottom: '12px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #1E293B' }}>
        {[
          { label: 'נוכחי', value: currentPos, color: lineColor },
          { label: 'הטוב ביותר', value: bestPos, color: '#22C55E' },
          { label: 'הגרוע ביותר', value: worstPos, color: '#EF4444' },
          {
            label: 'שינוי',
            value: totalChange > 0 ? '+' + totalChange : totalChange === 0 ? '0' : String(totalChange),
            color: totalChange > 0 ? '#22C55E' : totalChange < 0 ? '#EF4444' : '#8B99AE',
          },
          { label: 'נקודות', value: withPosition.length, color: '#8B99AE' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1,
            padding: '10px 8px',
            textAlign: 'center',
            background: '#0B0F1A',
            borderRight: i < 4 ? '1px solid #1E293B' : 'none',
          }}>
            <div style={{
              fontSize: '20px',
              fontWeight: 800,
              fontFamily: "'JetBrains Mono', monospace",
              color: s.color,
              lineHeight: 1,
            }}>
              {s.value}
            </div>
            <div style={{ fontSize: '10px', color: '#8B99AE', marginTop: '3px' }}>{s.label}</div>
          </div>
        ))}
      </div>

     {/* SVG Chart */}
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        style={{ width: '100%', height: 'auto', display: 'block' }}
        onMouseLeave={() => setHover(null)}
      >
        {/* Grid lines */}
        {yLabels.map(p => (
          <line
            key={p}
            x1={padLeft}
            y1={toY(p)}
            x2={chartWidth - padRight}
            y2={toY(p)}
            stroke="#1E293B"
            strokeWidth="1"
          />
        ))}

        {/* Top 10 zone */}
        {yMin <= 10 && (
          <rect
            x={padLeft}
            y={toY(Math.max(yMin, 1))}
            width={plotW}
            height={toY(Math.min(10, yMax)) - toY(Math.max(yMin, 1))}
            fill="rgba(34,197,94,0.04)"
          />
        )}

        {/* Area fill */}
        <path d={areaPath} fill={lineColor} fillOpacity="0.08" />

        {/* Line */}
        <path d={linePath} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" />

        {/* Data points */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={hover === i ? 5 : 2.5} fill={lineColor} stroke="#111827" strokeWidth={hover === i ? 2 : 1} />
        ))}

        {/* Invisible hover zones */}
        {points.map((p, i) => (
          <rect
            key={'h' + i}
            x={p.x - (plotW / points.length / 2)}
            y={padTop}
            width={plotW / points.length}
            height={plotH}
            fill="transparent"
            onMouseEnter={() => setHover(i)}
          />
        ))}

        {/* Hover vertical line */}
        {hover !== null && (
          <line x1={points[hover].x} y1={padTop} x2={points[hover].x} y2={padTop + plotH} stroke="#4B5C73" strokeWidth="1" strokeDasharray="3,3" />
        )}

        {/* Hover tooltip */}
        {hover !== null && (() => {
          const p = points[hover];
          const r = withPosition[hover];
          const tooltipW = 100;
          const tooltipH = 38;
          let tx = p.x - tooltipW / 2;
          if (tx < padLeft) tx = padLeft;
          if (tx + tooltipW > chartWidth - padRight) tx = chartWidth - padRight - tooltipW;
          const ty = p.y - tooltipH - 10;
          const finalTy = ty < 2 ? p.y + 10 : ty;
          return (
            <g>
              <rect x={tx} y={finalTy} width={tooltipW} height={tooltipH} rx="6" fill="#1E293B" stroke="#2D3A4F" strokeWidth="1" />
              <text x={tx + tooltipW / 2} y={finalTy + 15} textAnchor="middle" fill="#E2E8F0" fontSize="12" fontWeight="700" fontFamily="'JetBrains Mono', monospace">
                מיקום {r.position_desktop}
              </text>
              <text x={tx + tooltipW / 2} y={finalTy + 30} textAnchor="middle" fill="#8B99AE" fontSize="10" fontFamily="'JetBrains Mono', monospace">
                {r.date}
              </text>
            </g>
          );
        })()}

        {/* Y axis labels */}
        {yLabels.map(p => (
          <text key={p} x={padLeft - 6} y={toY(p) + 4} textAnchor="end" fill="#4B5C73" fontSize="10" fontFamily="'JetBrains Mono', monospace">
            {p}
          </text>
        ))}

        {/* X axis labels */}
        {xLabels.map((xl, i) => (
          <text key={i} x={xl.x} y={chartHeight - 4} textAnchor="middle" fill="#4B5C73" fontSize="10" fontFamily="'JetBrains Mono', monospace">
            {xl.label}
          </text>
        ))}

        {/* Current position highlight */}
        {hover === null && (
          <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="5" fill={lineColor} stroke="#111827" strokeWidth="2" />
        )}
      </svg>
    </div>
  );
}

const containerStyle = {
  background: '#0B0F1A',
  border: '1px solid #1E293B',
  borderTop: 'none',
  padding: '16px',
  animation: 'slideDown 0.2s ease-out',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '12px',
};

const closeBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#8B99AE',
  fontSize: '20px',
  cursor: 'pointer',
  padding: '0 4px',
  lineHeight: 1,
};